import React, { useState } from 'react';
import { XIcon, MailIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-12">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-13">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-14">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-15">
              <h3 className="text-2xl font-bold text-navy" data-id="element-16">{title}</h3>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-17">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-18" />
              </button>
            </div>
            <div className="p-6 text-charcoal text-lg leading-relaxed" data-id="element-19">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-gray-400 py-10 px-6 text-center" data-id="element-20">
        <div className="max-w-4xl mx-auto space-y-4" data-id="element-21">
          <p className="text-lg font-heading text-cream" data-id="element-22">
            El Family Vault Digital
          </p>
          <p className="text-sm" data-id="element-23">
            © {new Date().getFullYear()} Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-6 text-sm pt-4" data-id="element-24">
            <button onClick={() => setActiveModal('terms')} className="hover:text-cream transition-colors" data-id="element-25">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-cream transition-colors" data-id="element-26">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('contact')} className="hover:text-cream transition-colors" data-id="element-27">
              Contacto
            </button>
          </div>
        </div>
      </footer>

      {/* Términos y Condiciones */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-28">
        <div className="space-y-4" data-id="element-29">
          <p data-id="element-30">
            Al adquirir "El Family Vault Digital", aceptas los siguientes
            términos:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-31">
            <li data-id="element-32">
              Este es un producto digital. Una vez completada la compra,
              recibirás acceso inmediato al contenido.
            </li>
            <li data-id="element-33">
              El contenido es para uso personal y familiar. No está permitida su
              redistribución o reventa.
            </li>
            <li data-id="element-34">
              Los materiales se proporcionan "tal cual" con fines educativos y
              organizativos.
            </li>
            <li data-id="element-35">
              BS Digital Group no se hace responsable del uso indebido de la
              información proporcionada.
            </li>
            <li data-id="element-36">
              Nos reservamos el derecho de actualizar estos términos en
              cualquier momento.
            </li>
          </ul>
          <p className="pt-4 text-base text-gray-500" data-id="element-37">
            Para cualquier consulta, escríbenos a{' '}
            <a href="mailto:Support@bsdigitalgroup.com" className="text-gold font-bold hover:underline" data-id="element-38">
              Support@bsdigitalgroup.com
            </a>
          </p>
        </div>
      </Modal>

      {/* Política de Privacidad */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-39">
        <div className="space-y-4" data-id="element-40">
          <p data-id="element-41">Tu privacidad es importante para nosotros. En BS Digital Group:</p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-42">
            <li data-id="element-43">
              Solo recopilamos la información necesaria para procesar tu compra
              (nombre, correo electrónico, datos de pago).
            </li>
            <li data-id="element-44">
              No compartimos, vendemos ni alquilamos tu información personal a
              terceros.
            </li>
            <li data-id="element-45">
              Utilizamos encriptación segura para proteger tus datos durante las
              transacciones.
            </li>
            <li data-id="element-46">
              Puedes solicitar la eliminación de tus datos en cualquier momento
              contactándonos por correo.
            </li>
            <li data-id="element-47">
              Podemos enviar correos relacionados con tu compra y
              actualizaciones del producto.
            </li>
          </ul>
          <p className="pt-4 text-base text-gray-500" data-id="element-48">
            Para solicitudes de privacidad, escríbenos a{' '}
            <a href="mailto:Support@bsdigitalgroup.com" className="text-gold font-bold hover:underline" data-id="element-49">
              Support@bsdigitalgroup.com
            </a>
          </p>
        </div>
      </Modal>

      {/* Contacto */}
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contacto" data-id="element-50">
        <div className="space-y-6 text-center" data-id="element-51">
          <div className="bg-cream p-8 rounded-xl" data-id="element-52">
            <MailIcon className="w-12 h-12 text-gold mx-auto mb-4" data-id="element-53" />
            <p className="text-xl mb-2" data-id="element-54">¿Tienes preguntas o necesitas ayuda?</p>
            <p className="text-lg text-gray-600 mb-6" data-id="element-55">
              Escríbenos y te responderemos lo antes posible.
            </p>
            <a href="mailto:Support@bsdigitalgroup.com" className="inline-block bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-1 text-lg" data-id="element-56">
              Support@bsdigitalgroup.com
            </a>
          </div>
          <p className="text-base text-gray-500" data-id="element-57">
            Horario de atención: Lunes a Viernes, 9:00 AM - 5:00 PM (EST)
          </p>
        </div>
      </Modal>
    </>;
}